import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  useEmergencyCategoryStore,
  useEmergencySubcategoryStore,
} from "../../../store/masterDataStore";
import { EmergencySubcategoryType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm = {
  subcategoryName: "",
  subcategoryDescription: "",
  categoryId: "",
};

const EmergencySubCategory: React.FC = () => {
  const {
    createEmergencySubcategory,
    updateEmergencySubcategory,
    deleteEmergencySubcategory,
    emergencySubcategory,
    fetchAllEmergencySubcategory,
  } = useEmergencySubcategoryStore();
  const { emergencyCategory, fetchAllEmergencyCategory } =
    useEmergencyCategoryStore();
  const [rowData, setRowData] = useState<EmergencySubcategoryType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    const resData = emergencySubcategory.map((subcategory: any) => {
      return {
        ...subcategory,
        categoryName: emergencyCategory.find(
          (category: any) => category._id === subcategory.categoryId
        )?.categoryName,
      };
    });
    setRowData(resData);
  }, [emergencySubcategory, emergencyCategory]);

  useEffect(() => {
    const fetchData = async () => {
      if (!emergencyCategory.length) await fetchAllEmergencyCategory();
      await fetchAllEmergencySubcategory();
    };
    fetchData();
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      let response;
      try {
        if (isEditMode && editId) {
          response = await updateEmergencySubcategory({ ...formData }, editId);
        } else {
          response = await createEmergencySubcategory(formData);
        }
      } catch (error) {
        return false;
      }
      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        await fetchAllEmergencySubcategory();
        return true;
      }
      return false;
    },
    [
      isEditMode,
      editId,
      formData,
      updateEmergencySubcategory,
      createEmergencySubcategory,
    ]
  );

  const handleEdit = useCallback(
    (data: any) => {
      setIsEditMode(true);
      setEditId(data._id);
      setFormData({
        subcategoryName: data.subcategoryName,
        subcategoryDescription: data.subcategoryDescription,
        categoryId: data.categoryId,
      });
      setModalOpen(true);
    },
    [setIsEditMode, setEditId, setFormData]
  );
  const handleDelete = useCallback(
    (id: string) => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this subcategory?"
      );
      if (confirmed) {
        deleteEmergencySubcategory(id);
      }
    },
    [deleteEmergencySubcategory]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "subcategoryName",
        headerName: "Subcategory Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
        },
      },
      {
        field: "categoryName",
        headerName: "Category Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
        },
        valueGetter: (params: any) => {
          return params.data?.categoryName || "NA";
        },
      },
      {
        field: "subcategoryDescription",
        headerName: "Subcategory Description",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
        },
        tooltipField: "subcategoryDescription",
      },
      {
        field: "createdAt",
        headerName: "Created At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
        },
        valueGetter: (params: any) => {
          return (
            new Date(params.data?.createdAt).toLocaleDateString("en-US") || "NA"
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Emergency Subcategory"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Emergency Subcategory"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    [handleEdit, handleDelete]
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Emergency SubCategory</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setEditId(null);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} />
            Add SubCategory
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No Emergency sub-categories added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative"
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode
              ? "Update Emergency Subcategory"
              : "Create New Emergency Subcategory"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="px-6 pb-6 flex flex-col gap-3">
              {/* Category Dropdown */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="categoryId"
                  className="text-sm font-medium text-gray-700"
                >
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="categoryId"
                  name="categoryId"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.categoryId || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  required
                  aria-label="Category"
                  aria-required="true"
                >
                  <option value="" disabled>
                    Select Category
                  </option>
                  {emergencyCategory?.map((category: any) => (
                    <option key={category._id} value={category._id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="subcategoryName"
                  className="text-sm font-medium text-gray-700"
                >
                  Subcategory Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="subcategoryName"
                  name="subcategoryName"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.subcategoryName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subcategoryName: e.target.value,
                    })
                  }
                  placeholder="Enter subcategory name"
                  required
                  aria-label="Subcategory Name"
                  aria-required="true"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="subcategoryDescription"
                  className="text-sm font-medium text-gray-700"
                >
                  Subcategory Description
                </label>
                <textarea
                  id="subcategoryDescription"
                  name="subcategoryDescription"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  value={formData.subcategoryDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subcategoryDescription: e.target.value,
                    })
                  }
                  placeholder="Enter subcategory description (optional)"
                  rows={3}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
              >
                {isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default EmergencySubCategory;